<template>
    <div class="chart-container">
      <component
        v-if="hasValidData && chartReady"
        :is="chartComponent"
        :key="chartKey"
        :data="chartData"
        :options="chartOptions"
      />
      <div v-else class="no-data-message">
        {{ loadingMessage }}
      </div>
    </div>
  </template>
  
  <script lang="ts">
  import { Component, Vue, toNative, Prop, Watch } from 'vue-facing-decorator'
  import {
    Bar,
    Line,
    Pie,
    Doughnut,
    Radar,
    PolarArea,
    Bubble,
    Scatter,
  } from 'vue-chartjs'
  
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Legend,
    BarElement,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    ArcElement,
    Filler,
    ChartData,
    ChartOptions,
  } from 'chart.js'
  
  ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    ArcElement,
    Filler
  )
  
  type ChartType = 'bar' | 'line' | 'pie' | 'doughnut' | 'radar' | 'polarArea' | 'bubble' | 'scatter'
  
  @Component({
    name: 'MetricChart',
    components: {
      Bar,
      Line,
      Pie,
      Doughnut,
      Radar,
      PolarArea,
      Bubble,
      Scatter,
    }
  })
  class MetricChart extends Vue {
    @Prop({
      type: Array,
      required: true,
      default: () => [],
      validator: (value: Array<Record<string, any>>) => Array.isArray(value)
    })
    data!: Array<Record<string, any>>
    
    @Prop({
      type: String,
      default: 'bar',
      validator: (value: string) => ['bar', 'line', 'pie', 'doughnut', 'radar', 'polarArea', 'bubble', 'scatter'].includes(value)
    })
    chartType!: ChartType
    
    @Prop({
      type: String,
      default: 'date'
    })
    xKey!: string
    
    @Prop({
      type: String,
      default: 'value'
    })
    yKey!: string
  
    @Prop({
      type: String,
      default: 'Loading chart data...'
    })
    loadingMessage!: string
  
    chartReady = false
    chartKey = 0
  
    get hasValidData(): boolean {
      return Array.isArray(this.data) && 
             this.data.length > 0 && 
             this.data.every(item => item[this.xKey] !== undefined && item[this.yKey] !== undefined)
    }
  
    mounted() {
      this.initializeChart()
    }
  
    @Watch('data', { deep: true })
    @Watch('chartType')
    onDataChange() {
      this.chartReady = false
      this.chartKey++
      this.$nextTick(() => {
        this.initializeChart()
      })
    }
  
    initializeChart() {
      if (this.hasValidData) {
        this.chartReady = true
      }
    }
  
    get chartComponent(): Vue {
      const components = {
        bar: Bar,
        line: Line,
        pie: Pie,
        doughnut: Doughnut,
        radar: Radar,
        polarArea: PolarArea,
        bubble: Bubble,
        scatter: Scatter,
      }
      return components[this.chartType] || Bar
    }
  
    get chartData(): ChartData {
      if (!this.hasValidData) {
        return {
          labels: [],
          datasets: [{
            label: this.yKey,
            data: [],
            backgroundColor: 'rgba(61, 90, 0, 0.1)',
            borderColor: '#3D5A00',
            borderWidth: 2
          }]
        }
      }
  
      return {
        labels: this.data.map(item => {
          try {
            const date = new Date(item[this.xKey])
            return isNaN(date.getTime()) ? item[this.xKey] : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          } catch (e) {
            return item[this.xKey]
          }
        }),
        datasets: [{
          label: this.yKey,
          data: this.data.map(item => Number(item[this.yKey]) || 0),
          backgroundColor: 'rgba(61, 90, 0, 0.08)',
          borderColor: '#3D5A00',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#3D5A00',
          pointRadius: 3,
          pointHoverRadius: 6,
        }]
      }
    }
  
    get chartOptions(): ChartOptions {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 0
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: (context) => {
                return `${context.dataset.label}: ${context.raw}`
              }
            }
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0
            }
          }
        }
      }
    }
  }
  
  export default toNative(MetricChart)
  </script>
  
  <style scoped>
  .chart-container {
    height: 300px;
    position: relative;
  }
  
  .no-data-message {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #6c757d;
    font-style: italic;
  }
  </style>